import * as React from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { FormattedMessage } from "react-intl";
import { ApplicationState } from "stores";
import { createDirtyUser, changeUserField, saveUser } from "stores/user";
import { HooplaUser } from "types";
import Input, { LabeledInput } from "components/Input";
import { ContainerLoader } from "components/Loading";
import Button from "components/Button";
import Typography from "components/Typography";
import PageHeader from "components/PageHeader";
import * as styles from "./styles";

interface IProps {
  history: any;
  user: HooplaUser;
  loading: boolean;
  patching: boolean;
  error: boolean;
  createDirtyUser: () => void;
  changeUserField: (key: string, value: string) => void;
  saveUser: (
    userId: number,
    firstName: string,
    lastName: string,
    phoneNumber: string
  ) => void;
}

const countryCodes = [
  { value: "+47", label: "+47" },
  { value: "+46", label: "+46" },
  { value: "+45", label: "+45" }
];

const validatePhoneNumberInput = (value: string) =>
  value === "" || value.split("").every(x => String(parseInt(x, 10)) === x);

class Settings extends React.Component<IProps, {}> {
  componentDidMount() {
    this.props.createDirtyUser();
  }

  handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { userId, firstName, lastName, phoneNumber } = this.props.user;
    this.props.saveUser(userId, firstName, lastName, phoneNumber);
  };

  handleChangeField = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.changeUserField(key, e.target.value);
  };

  handleChangePhoneNumber = (phoneNumber: string) => {
    this.props.changeUserField("phoneNumber", phoneNumber);
  };

  handleCancel = () => {
    this.props.history.push({ pathname: "/" });
  };

  render() {
    if (this.props.loading) {
      return <ContainerLoader />;
    }

    const phoneNumberMatch = (this.props.user.phoneNumber || "")
      .replace(/ /g, "")
      .match(/^(\+\d{2})?(\d+)?$/);
    const countryCode = phoneNumberMatch[1] || countryCodes[0].value;
    const phoneNumber = phoneNumberMatch[2] || "";

    return (
      <React.Fragment>
        <PageHeader>
          <FormattedMessage id="containers.Settings.yourProfile" />
        </PageHeader>

        <form onSubmit={this.handleSubmit}>
          <div className={styles.row}>
            <LabeledInput
              id="firstName"
              label={<FormattedMessage id="containers.Settings.firstName" />}
              value={this.props.user.firstName || ""}
              onChange={e => this.handleChangeField("firstName", e)}
            />
            <LabeledInput
              id="lastName"
              label={<FormattedMessage id="containers.Settings.lastName" />}
              value={this.props.user.lastName || ""}
              onChange={e => this.handleChangeField("lastName", e)}
            />
          </div>
          <div className={styles.row}>
            <LabeledInput
              id="email"
              label={<FormattedMessage id="containers.Settings.email" />}
              value={this.props.user.email || ""}
              onChange={() => {}}
              disabled={true}
            />
            <div className={styles.labeledInput}>
              <label htmlFor="phoneNumber">
                <FormattedMessage id="containers.Settings.phoneNumber" />
              </label>
              <div className={styles.phoneNumberInput}>
                <Select
                  placeholder="+47"
                  options={countryCodes}
                  value={countryCodes.find(x => x.value === countryCode)}
                  styles={styles.selectStyles}
                  isSearchable={false}
                  isClearable={false}
                  onChange={(x: { value: string; label: string }) =>
                    this.handleChangePhoneNumber(`${x.value}${phoneNumber}`)
                  }
                />
                <Input
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={e => {
                    if (validatePhoneNumberInput(e.target.value)) {
                      this.handleChangePhoneNumber(
                        `${countryCode || ""}${e.target.value}`
                      );
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            {this.props.error && (
              <div className={styles.error}>
                <Typography color="red">
                  <FormattedMessage id="containers.Settings.genericError" />
                </Typography>
              </div>
            )}

            <div className={styles.actions}>
              <Button variant="outlined" onClick={this.handleCancel}>
                <FormattedMessage id="containers.Settings.cancel" />
              </Button>
              <Button
                  variant={"lightblue"}
                onClick={this.handleSubmit}
                type="submit"
                loading={this.props.patching}
              >
                <FormattedMessage id="containers.Settings.save" />
              </Button>
            </div>
          </div>
        </form>
      </React.Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    loading:
      state.user.fetching ||
      !state.user.fetched ||
      !state.user.dirtyUserCreated,
    patching: state.user.patching,
    user: state.user.dirtyUser,
    error: state.user.error
  }),
  { createDirtyUser, changeUserField, saveUser }
)(Settings);
